import styled, { CSSObject } from '@emotion/styled';
import classNames from 'classnames';
import { ButtonHTMLAttributes, FC, forwardRef, HTMLProps, useCallback } from 'react';

import { EvtKeys } from 'app/types';

import MenuButton, { MenuButtonProps, menuButtonWidth } from './MenuButton';
import { Theme } from '../../theme';
import { getSemanticColorVariable, SemanticColorKey } from '../../theme/colors';
import { MarginProps } from '../Box';
import { getSpace } from '../Box/helpers';
import Flex from '../Flex';
import Icon from '../Icon';
import { IconName } from '../Icon/types';
import LoadingSpinner from '../LoadingSpinner';
import { defaultTextFontFamily } from '../Text';

// Local Typings
export type ButtonColor = 'brand' | 'destruct' | 'icon_button_utility';
type ButtonHierarchy = 'primary' | 'secondary' | 'tertiary';
type ButtonSize = keyof Theme['components']['button']['size'];
interface ButtonPadding {
  lg: number;
  md: number;
  sm: number;
}
export interface AriaButtonProps {
  ariaExpanded?: boolean;
  ariaHasPopUp?: boolean;
  ariaLabel?: string;
  ariaPressed?: boolean;
  ariaSelected?: boolean;
}
interface StyledButtonProps extends MarginProps {
  backgroundColor?: SemanticColorKey;
  buttonColor?: ButtonColor;
  buttonSize?: ButtonSize;
  circle: boolean;
  collapse: boolean;
  flexBasis?: number | string;
  fullStoryMask?: boolean;
  fullWidth: boolean;
  fullWidthOnMobile?: boolean;
  hierarchy: ButtonHierarchy;
  maxWidth?: number;
  minWidth?: number;
  minimize?: boolean;
  pointerEvents: CSSObject['pointerEvents'];
  withLargePadding: boolean;
  withMenuButton: boolean;
  withPaddingY?: boolean;
  withRoundBorder?: boolean;
}
interface StyledFlexProps {
  isLoading: boolean;
}
export interface ButtonProps extends HTMLProps<HTMLButtonElement>,
  AriaButtonProps,
  Partial<Omit<StyledButtonProps, 'withMenuButton'>>,
  Partial<StyledFlexProps> {
  circle?: boolean;
  leftIcon?: IconName;
  menuButtonProps?: Omit<MenuButtonProps, 'disabled' | 'isLoading'>;
  minimize?: boolean;
  navigate?: () => void; // This is used by React Router's Link component
  rightIcon?: IconName;
  shouldPropagate?: boolean;
  testId?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

// Local Variables
const buttonFontSizes: Record<ButtonSize, number> = {
  lg: 16,
  md: 14,
  sm: 12,
};
const borderWidth = 2;
const borderBase = `${borderWidth}px solid`;
const buttonPadding: ButtonPadding = {
  lg: 48,
  md: 16,
  sm: 8,
};
const getBorderRadius = (props: StyledButtonProps & { theme: Theme }) => {
  if (props.buttonColor === 'icon_button_utility') {
    return 4;
  }

  return (props.circle || props.withRoundBorder) ? 50 : props.theme.components.button.borderRadius;
};
const getPadding = (props: StyledButtonProps) => {
  if (props.circle || props.minimize) {
    return;
  }
  if (props.collapse) {
    return 0;
  }
  if (props.buttonSize === 'sm') {
    return buttonPadding.sm;
  }
  if (props.withLargePadding) {
    return buttonPadding.lg;
  }

  return buttonPadding.md;
};
const getWidth = (props: StyledButtonProps & { theme: Theme }) => {
  if (props.circle || props.buttonColor === 'icon_button_utility') {
    return props.theme.components.button.size[props.buttonSize ?? 'lg'];
  }

  return props.fullWidth ? '100%' : 'min-content';
};
/* eslint-disable @typescript-eslint/no-explicit-any */
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getInitialPrimaryColors = (props: StyledButtonProps) => {
  const buttonColorValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_default')
    : getSemanticColorVariable('button_default');

  const backgroundColor = props.backgroundColor ? getSemanticColorVariable(props.backgroundColor) : buttonColorValue;
  const textColor = getSemanticColorVariable('button_text_default');

  return {
    backgroundColor,
    border: `${borderBase} ${buttonColorValue}`,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getInitialSecondaryColors = (props: StyledButtonProps) => {
  const buttonColorVariable: SemanticColorKey = props.buttonColor === 'destruct'
    ? 'danger_button_default'
    : 'button_secondary_border';

  const buttonColorValue = getSemanticColorVariable(buttonColorVariable);
  const buttonTextColorVariable: SemanticColorKey = props.buttonColor === 'destruct'
    ? 'danger_button_secondary_text_default'
    : 'button_secondary_text_default';

  const textColor = getSemanticColorVariable(buttonTextColorVariable);

  return {
    backgroundColor: 'transparent',
    border: `${borderBase} ${buttonColorValue}`,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getInitialTertiaryColors = (props: StyledButtonProps) => {
  const textColorVariable: SemanticColorKey = props.buttonColor === 'destruct'
    ? 'danger_button_default'
    : 'button_tertiary_text_default';

  const textColor = getSemanticColorVariable(textColorVariable);

  return {
    backgroundColor: 'transparent',
    border: `${borderBase} transparent`,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getInitialColors = (props: StyledButtonProps) => {
  if (props.buttonColor === 'icon_button_utility') {
    const textColor = getSemanticColorVariable('utility_button_icon_default');
    const borderColor = getSemanticColorVariable('utility_button_border_default');

    return {
      backgroundColor: 'transparent',
      border: `${borderBase} ${borderColor}`,
      color: textColor,
      ['--button-content-color' as any]: textColor,
    };
  }

  switch (props.hierarchy) {
    case 'secondary':
      return getInitialSecondaryColors(props);
    case 'tertiary':
      return getInitialTertiaryColors(props);
    default:
      return getInitialPrimaryColors(props);
  }
};

// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getHoverPrimaryColors = (props: StyledButtonProps) => {
  const buttonColorValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_hover')
    : getSemanticColorVariable('button_hover');

  const textColor = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_text_hover')
    : getSemanticColorVariable('button_text_hover');

  return {
    backgroundColor: buttonColorValue,
    borderColor: buttonColorValue,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getHoverSecondaryColors = (props: StyledButtonProps) => {
  const buttonColorValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_hover')
    : getSemanticColorVariable('button_hover');
  const textColor = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_secondary_text_hover')
    : getSemanticColorVariable('button_secondary_text_hover');

  return {
    backgroundColor: 'transparent',
    border: `${borderBase} ${buttonColorValue}`,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getTertiaryHoverColors = (props: StyledButtonProps) => {
  const textColor = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_hover')
    : getSemanticColorVariable('button_hover');

  return {
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getHoverColors = (props: StyledButtonProps) => {
  if (props.buttonColor === 'icon_button_utility') {
    return {
      backgroundColor: getSemanticColorVariable('utility_button_fill_hover'),
      borderColor: getSemanticColorVariable('utility_button_border_hover'),
    };
  }

  switch (props.hierarchy) {
    case 'secondary':
      return getHoverSecondaryColors(props);
    case 'tertiary':
      return getTertiaryHoverColors(props);
    default:
      return getHoverPrimaryColors(props);
  }
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getPrimaryActiveColors = (props: StyledButtonProps) => {
  const buttonColorValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_active')
    : getSemanticColorVariable('button_active');
  const textColor = getSemanticColorVariable('button_text_active');

  return {
    backgroundColor: buttonColorValue,
    borderColor: buttonColorValue,
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getSecondaryActiveColors = (props: StyledButtonProps) => {
  const buttonBorderValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_active')
    : getSemanticColorVariable('button_active');
  const buttonColorValue = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_secondary_text_active')
    : getSemanticColorVariable('button_secondary_text_active');

  return {
    border: `${borderBase} ${buttonBorderValue}`,
    color: buttonColorValue,
    ['--button-content-color' as any]: buttonColorValue,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getTertiaryActiveColors = (props: StyledButtonProps) => {
  const textColor = props.buttonColor === 'destruct'
    ? getSemanticColorVariable('danger_button_active')
    : getSemanticColorVariable('button_active');

  return {
    color: textColor,
    ['--button-content-color' as any]: textColor,
  };
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getActiveColors = (props: StyledButtonProps) => {
  if (props.buttonColor === 'icon_button_utility') {
    return {
      backgroundColor: getSemanticColorVariable('utility_button_fill_active'),
    };
  }

  switch (props.hierarchy) {
    case 'secondary':
      return getSecondaryActiveColors(props);
    case 'tertiary':
      return getTertiaryActiveColors(props);
    default:
      return getPrimaryActiveColors(props);
  }
};
// only exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export const getDisabledColors = (props: StyledButtonProps) => {
  const buttonColorValue = getSemanticColorVariable('button_disabled');
  const textColor = getSemanticColorVariable('button_text_disabled');

  switch (props.hierarchy) {
    case 'secondary':
      return {
        backgroundColor: 'transparent',
        borderColor: buttonColorValue,
        color: textColor,
        ['--button-content-color' as any]: textColor,
      };
    case 'tertiary':
      return {
        color: textColor,
        ['--button-content-color' as any]: textColor,
      };
    default:
      return {
        backgroundColor: buttonColorValue,
        borderColor: buttonColorValue,
        color: textColor,
        ['--button-content-color' as any]: textColor,
      };
  }
};
const transition = '250ms';
const StyledButton = styled.button<StyledButtonProps>(props => ({
  alignItems: 'center',
  borderRadius: getBorderRadius(props),
  cursor: 'pointer',
  display: 'flex',
  flexBasis: props.flexBasis,
  fontSize: buttonFontSizes[props.buttonSize ?? 'lg'],
  fontWeight: 'bold',
  height: props.collapse ? undefined : props.theme.components.button.size[props.buttonSize ?? 'lg'],
  justifyContent: 'center',
  marginBottom: getSpace([props.marginBottom, props.marginY, props.margin],
    props.theme.spacing),
  marginLeft: getSpace([props.marginLeft, props.marginX, props.margin],
    props.theme.spacing),
  marginRight: getSpace([props.marginRight, props.marginX, props.margin],
    props.theme.spacing),
  marginTop: getSpace([props.marginTop, props.marginY, props.margin],
    props.theme.spacing),
  maxWidth: props.maxWidth,
  minWidth: props.minWidth,
  outlineColor: getSemanticColorVariable('border_focus'),
  overflow: 'visible',
  ...(props.buttonColor === 'icon_button_utility'
    ? {
      padding: 8,
    }
    : {
      paddingBottom: props.withPaddingY ? 4 : undefined,
      paddingLeft: getPadding(props),
      paddingRight: getPadding(props),
      paddingTop: props.withPaddingY ? 4 : undefined,
    }),
  pointerEvents: props.pointerEvents,
  position: 'relative',
  textDecorationLine: 'none',
  transition,
  whiteSpace: 'nowrap',
  width: getWidth(props),
  ...getInitialColors(props),
  ...(props.withMenuButton
    ? {
      borderBottomRightRadius: 0,
      borderRight: 0,
      borderTopRightRadius: 0,
      marginRight: menuButtonWidth,
    }
    : {}),

  /* eslint-disable sort-keys,@typescript-eslint/naming-convention */
  '& svg g, svg:not(g) > path, svg:not(g) > circle': {
    transition,
  },

  '&:hover': {
    ...getHoverColors(props),
  },

  '&:active': {
    ...getActiveColors(props),
  },

  '&:disabled': {
    ...getDisabledColors(props),
    cursor: 'not-allowed',
  },

  [props.theme.components.mediaQueries.xs.max]: {
    width: (props.circle || props.minimize || !props.fullWidthOnMobile) ? getWidth(props) : '100%',
  },
  /* eslint-enable sort-keys,@typescript-eslint/naming-convention */
}));
const StyledFlex = styled(Flex)<StyledFlexProps>(props => ({
  visibility: props.isLoading ? 'hidden' : undefined,
}));

// Component Definition
const Button: FC<ButtonProps> = forwardRef(({
  buttonColor = 'brand',
  buttonSize = 'lg',
  circle = false,
  collapse = false,
  disabled = false,
  fullStoryMask,
  fullWidth = false,
  fullWidthOnMobile = true,
  hierarchy = 'primary',
  isLoading = false,
  leftIcon,
  menuButtonProps,
  minimize,
  navigate,
  onClick,
  pointerEvents = 'auto',
  rightIcon,
  shouldPropagate,
  testId,
  type = 'button',
  withLargePadding = false,
  ...props
},
ref) => {
  const handleClick = useCallback((evt: React.MouseEvent<HTMLButtonElement>) => {
    if (navigate) {
      evt.preventDefault();

      navigate();
    } else if (onClick) {
      if (!shouldPropagate) {
        evt.stopPropagation();
      }
      onClick(evt);
    } else if (props.href) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      props.target
        ? window.open(props.href,
          props.target)
        : window.location.href = props.href;
    }
  },
  [navigate, onClick, props.href, props.target, shouldPropagate]);

  const handleKeyDown = useCallback((evt: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!shouldPropagate) {
      evt.stopPropagation();
    }
    if (navigate && (evt.key as EvtKeys) === (EvtKeys.Space || EvtKeys.Enter)) {
      evt.preventDefault();

      navigate();
    }
  },
  [navigate, shouldPropagate]);

  const className = fullStoryMask
    ? classNames(props.className, 'button', 'fs-exclude')
    : classNames(props.className, 'button');

  const content = (
    <StyledButton
      {...props}
      aria-expanded={props.ariaExpanded}
      aria-haspopup={props.ariaHasPopUp}
      aria-label={props.ariaLabel}
      aria-pressed={props.ariaPressed}
      aria-selected={props.ariaSelected}
      as="button" // needed to make the TS compiler happy
      buttonColor={buttonColor}
      buttonSize={buttonSize}
      circle={circle}
      className={className}
      collapse={collapse}
      data-testid={testId}
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      fullWidthOnMobile={fullWidthOnMobile}
      hierarchy={hierarchy}
      minimize={minimize}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      pointerEvents={pointerEvents}
      ref={ref}
      type={type}
      withLargePadding={withLargePadding}
      withMenuButton={!!menuButtonProps}
    >
      {isLoading && (
        <Flex
          bottom={0}
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
          top={0}
        >
          <LoadingSpinner
            iconSize={buttonSize === 'sm' ? 'xxs' : undefined}
          />
        </Flex>
      )}

      <StyledFlex
        as="span"
        fontFamily={defaultTextFontFamily}
        gap={2}
        isLoading={isLoading}
        justifyContent="space-between"
        lineHeight="1.0em"
      >
        {leftIcon && (
          <Icon
            name={leftIcon}
            size="xxs"
          />
        )}

        {props.children}

        {rightIcon && (
          <Icon
            name={rightIcon}
            size="xxs"
          />
        )}
      </StyledFlex>
    </StyledButton>
  );

  return menuButtonProps
    ? (
      <MenuButton
        {...menuButtonProps}
        disabled={disabled}
        isLoading={isLoading}
      >
        {content}
      </MenuButton>
    )
    : content;
});

export default Button;
